import { useCallback, useEffect, useRef, useState } from "react";
import type { IBatchDelete, IBatchDeletePayload, IBatchDeleteResultItem } from "@types";
import { createBatchDelete, getBatchDelete, getBatchDeleteItems } from "@services/form";
import { getApiErrorCode } from "@utils/apiError";

export const BATCH_DELETE_POLLING_INTERVAL_MS = 2000;
export const BATCH_DELETE_MAX_POLLING_FAILURES = 3;
export const BATCH_DELETE_ITEMS_PAGE_SIZE = 100;

export type BatchDeletePhase = "idle" | "submitting" | "processing" | "finished" | "failed";

export interface BatchDeleteProcessState {
  phase: BatchDeletePhase;
  batch: IBatchDelete | null;
  notDeletedItems: IBatchDeleteResultItem[];
  notDeletedPage: number;
  hasMoreNotDeleted: boolean;
  isLoadingNotDeleted: boolean;
}

export interface BatchDeleteSubmitError {
  code: string | null;
  message: string | null;
}

const INITIAL_STATE: BatchDeleteProcessState = {
  phase: "idle",
  batch: null,
  notDeletedItems: [],
  notDeletedPage: 0,
  hasMoreNotDeleted: false,
  isLoadingNotDeleted: false,
};

export const createIdempotencyKey = (): string => {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const resolveErrorDetail = (error: unknown): string | null => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail.trim().length > 0 ? detail : null;
};

const hasNotDeleted = (batch: IBatchDelete) =>
  batch.status === "COMPLETED_WITH_ERRORS" || (batch.summary?.notDeleted ?? 0) > 0;

export const useBatchDeleteProcess = () => {
  const [state, setState] = useState<BatchDeleteProcessState>(INITIAL_STATE);
  const runRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idempotencyRef = useRef<{ signature: string; key: string } | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const loadNotDeleted = useCallback(async (batchId: string, page: number, run: number) => {
    setState((prev) => ({ ...prev, isLoadingNotDeleted: true }));

    try {
      const result = await getBatchDeleteItems(batchId, "NOT_DELETED", page, BATCH_DELETE_ITEMS_PAGE_SIZE);
      if (run !== runRef.current) return;

      setState((prev) => ({
        ...prev,
        notDeletedItems: page === 0 ? result.content : [...prev.notDeletedItems, ...result.content],
        notDeletedPage: page,
        hasMoreNotDeleted: !result.last && result.content.length > 0,
        isLoadingNotDeleted: false,
      }));
    } catch {
      if (run !== runRef.current) return;
      setState((prev) => ({ ...prev, isLoadingNotDeleted: false, hasMoreNotDeleted: false }));
    }
  }, []);

  const finish = useCallback(
    (batch: IBatchDelete, run: number) => {
      setState((prev) => ({ ...prev, phase: batch.status === "FAILED" ? "failed" : "finished", batch }));

      if (batch.status !== "FAILED" && hasNotDeleted(batch)) loadNotDeleted(batch.batchId, 0, run);
    },
    [loadNotDeleted]
  );

  const poll = useCallback(
    (batchId: string, run: number, failures = 0) => {
      timerRef.current = setTimeout(async () => {
        try {
          const batch = await getBatchDelete(batchId);
          if (run !== runRef.current) return;

          if (batch.status === "PROCESSING") {
            setState((prev) => ({ ...prev, batch }));
            poll(batchId, run);
            return;
          }

          finish(batch, run);
        } catch {
          if (run !== runRef.current) return;

          if (failures + 1 >= BATCH_DELETE_MAX_POLLING_FAILURES) {
            setState((prev) => ({ ...prev, phase: "failed" }));
            return;
          }

          poll(batchId, run, failures + 1);
        }
      }, BATCH_DELETE_POLLING_INTERVAL_MS);
    },
    [finish]
  );

  const submit = useCallback(
    async (payload: IBatchDeletePayload): Promise<BatchDeleteSubmitError | null> => {
      stopPolling();
      const run = ++runRef.current;
      const signature = JSON.stringify(payload);

      if (idempotencyRef.current?.signature !== signature) {
        idempotencyRef.current = { signature, key: createIdempotencyKey() };
      }

      setState({ ...INITIAL_STATE, phase: "submitting" });

      try {
        const batch = await createBatchDelete(payload, idempotencyRef.current.key);
        if (run !== runRef.current) return null;

        if (batch?.status === "PROCESSING") {
          setState((prev) => ({ ...prev, phase: "processing", batch }));
          poll(batch.batchId, run);
          return null;
        }

        if (batch?.batchId) {
          finish(batch, run);
          return null;
        }

        setState(INITIAL_STATE);
        return { code: null, message: null };
      } catch (error) {
        if (run === runRef.current) setState(INITIAL_STATE);
        return { code: getApiErrorCode(error), message: resolveErrorDetail(error) };
      }
    },
    [finish, poll, stopPolling]
  );

  const loadMoreNotDeleted = useCallback(() => {
    if (!state.batch || state.isLoadingNotDeleted || !state.hasMoreNotDeleted) return;
    loadNotDeleted(state.batch.batchId, state.notDeletedPage + 1, runRef.current);
  }, [loadNotDeleted, state.batch, state.hasMoreNotDeleted, state.isLoadingNotDeleted, state.notDeletedPage]);

  const reset = useCallback(() => {
    stopPolling();
    runRef.current += 1;
    idempotencyRef.current = null;
    setState(INITIAL_STATE);
  }, [stopPolling]);

  useEffect(
    () => () => {
      runRef.current += 1;
      stopPolling();
    },
    [stopPolling]
  );

  return { state, submit, loadMoreNotDeleted, reset };
};
